import React, { useState } from 'react';
import { Plus, Trash2, Upload, X } from 'lucide-react';

const ServicesTab = ({ 
  services, 
  setServices, 
  handleServicesSubmit, 
  isSubmitting,
  handleImageUpload,
  uploading 
}) => {
  const addService = () => {
    const newService = {
      id: Date.now().toString(),
      name_de: '',
      name_ru: '',
      description_de: '',
      description_ru: '',
      price: '',
      features_de: [''],
      features_ru: [''],
      image: '',
      active: true,
      tariffs: []
    };
    setServices([...services, newService]);
  };

  const removeService = (index) => {
    const updatedServices = services.filter((_, i) => i !== index);
    setServices(updatedServices);
  };

  const updateService = (index, field, value) => {
    const updatedServices = [...services];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServices(updatedServices);
  };

  const addFeature = (serviceIndex, language) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex][`features_${language}`].push('');
    setServices(updatedServices);
  };

  const removeFeature = (serviceIndex, featureIndex, language) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex][`features_${language}`] = 
      updatedServices[serviceIndex][`features_${language}`].filter((_, i) => i !== featureIndex);
    setServices(updatedServices);
  };

  const updateFeature = (serviceIndex, featureIndex, language, value) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex][`features_${language}`][featureIndex] = value;
    setServices(updatedServices);
  };

  const addTariff = (serviceIndex) => {
    const newTariff = {
      id: Date.now().toString(),
      name_de: '',
      name_ru: '',
      price: '',
      features_de: [''],
      features_ru: [''],
      conditions_de: '',
      conditions_ru: '',
      popular: false
    };
    const updatedServices = [...services];
    updatedServices[serviceIndex].tariffs = updatedServices[serviceIndex].tariffs || [];
    updatedServices[serviceIndex].tariffs.push(newTariff);
    setServices(updatedServices);
  };

  const removeTariff = (serviceIndex, tariffIndex) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex].tariffs = updatedServices[serviceIndex].tariffs.filter((_, i) => i !== tariffIndex);
    setServices(updatedServices);
  };

  const updateTariff = (serviceIndex, tariffIndex, field, value) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex].tariffs[tariffIndex] = {
      ...updatedServices[serviceIndex].tariffs[tariffIndex],
      [field]: value
    };
    setServices(updatedServices);
  };

  const addTariffFeature = (serviceIndex, tariffIndex, language) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex].tariffs[tariffIndex][`features_${language}`].push('');
    setServices(updatedServices);
  };

  const removeTariffFeature = (serviceIndex, tariffIndex, featureIndex, language) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex].tariffs[tariffIndex][`features_${language}`] = 
      updatedServices[serviceIndex].tariffs[tariffIndex][`features_${language}`].filter((_, i) => i !== featureIndex);
    setServices(updatedServices);
  };

  const updateTariffFeature = (serviceIndex, tariffIndex, featureIndex, language, value) => {
    const updatedServices = [...services];
    updatedServices[serviceIndex].tariffs[tariffIndex][`features_${language}`][featureIndex] = value;
    setServices(updatedServices);
  };

  return (
    <form onSubmit={handleServicesSubmit} className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-white">Управление услугами</h3>
          <p className="text-gray-400 text-sm">Управляйте вашими фотографическими услугами, ценами и описаниями.</p>
        </div>
        <button
          type="button"
          onClick={addService}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Добавить услугу
        </button>
      </div>

      <div className="space-y-8">
        {services.map((service, serviceIndex) => (
          <div key={service.id} className="bg-gray-900 p-6 border border-gray-700 rounded-lg">
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-md font-semibold text-white">Service {serviceIndex + 1}</h4>
              <button
                type="button"
                onClick={() => removeService(serviceIndex)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* German Content */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-gray-300">🇩🇪 Немецкий контент</h5>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Название услуги (немецкий)</label>
                  <input
                    type="text"
                    value={service.name_de}
                    onChange={(e) => updateService(serviceIndex, 'name_de', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Hochzeitsfotografie"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Описание (немецкий)</label>
                  <textarea
                    value={service.description_de}
                    onChange={(e) => updateService(serviceIndex, 'description_de', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Описание услуги на немецком языке..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Особенности (немецкий)</label>
                  {service.features_de.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(serviceIndex, featureIndex, 'de', e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Описание особенности..."
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(serviceIndex, featureIndex, 'de')}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addFeature(serviceIndex, 'de')}
                    className="text-sm text-indigo-400 hover:text-indigo-300"
                  >
                    + Добавить особенность
                  </button>
                </div>
              </div>

              {/* Russian Content */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-gray-300">🇷🇺 Русский контент</h5>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Название услуги (русский)</label>
                  <input
                    type="text"
                    value={service.name_ru}
                    onChange={(e) => updateService(serviceIndex, 'name_ru', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Свадебная фотография"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Описание (русский)</label>
                  <textarea
                    value={service.description_ru}
                    onChange={(e) => updateService(serviceIndex, 'description_ru', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Описание услуги на русском языке..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Особенности (русский)</label>
                  {service.features_ru.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(serviceIndex, featureIndex, 'ru', e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Описание особенности..."
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(serviceIndex, featureIndex, 'ru')}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addFeature(serviceIndex, 'ru')}
                    className="text-sm text-indigo-400 hover:text-indigo-300"
                  >
                    + Добавить особенность
                  </button>
                </div>
              </div>
            </div>

            {/* Price and Image */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-700">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Цена</label>
                <input
                  type="text"
                  value={service.price}
                  onChange={(e) => updateService(serviceIndex, 'price', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., ab €1.500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Изображение услуги</label>
                <div className="space-y-3">
                  {service.image && (
                    <div className="relative group">
                      <img 
                        src={service.image} 
                        alt="Service" 
                        className="w-full h-24 object-cover rounded-md border border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={() => updateService(serviceIndex, 'image', '')}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(`service_${serviceIndex}`, e.target.files[0])}
                      className="hidden"
                      id={`service-${serviceIndex}-upload`}
                      disabled={uploading === `service_${serviceIndex}`}
                    />
                    <label 
                      htmlFor={`service-${serviceIndex}-upload`}
                      className="flex items-center px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md cursor-pointer hover:bg-gray-600"
                    >
                      {uploading === `service_${serviceIndex}` ? (
                        <>Загрузка...</>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Загрузить изображение
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Tariffs Section */}
            <div className="mt-6 pt-6 border-t border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h5 className="text-md font-medium text-white">Тарифы услуги</h5>
                <button
                  type="button"
                  onClick={() => addTariff(serviceIndex)}
                  className="flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Добавить тариф
                </button>
              </div>

              {service.tariffs && service.tariffs.length > 0 ? (
                <div className="space-y-4">
                  {service.tariffs.map((tariff, tariffIndex) => (
                    <div key={tariff.id} className="bg-gray-800 p-4 border border-gray-600 rounded-md">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-medium text-gray-300">Тариф {tariffIndex + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeTariff(serviceIndex, tariffIndex)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">🇩🇪 Name</label>
                          <input
                            type="text"
                            value={tariff.name_de}
                            onChange={(e) => updateTariff(serviceIndex, tariffIndex, 'name_de', e.target.value)}
                            className="w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="Basic"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">🇷🇺 Name</label>
                          <input
                            type="text"
                            value={tariff.name_ru}
                            onChange={(e) => updateTariff(serviceIndex, tariffIndex, 'name_ru', e.target.value)}
                            className="w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="Базовый"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-300 mb-1">Price</label>
                          <input
                            type="text"
                            value={tariff.price}
                            onChange={(e) => updateTariff(serviceIndex, tariffIndex, 'price', e.target.value)}
                            className="w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="ab €150"
                          />
                        </div>
                      </div>

                      {/* Tariff Description */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">🇩🇪 Description</label>
                          <textarea
                            value={tariff.conditions_de || ''}
                            onChange={(e) => updateTariff(serviceIndex, tariffIndex, 'conditions_de', e.target.value)}
                            className="w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                            placeholder="Beschreibung des Tarifs..."
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">🇷🇺 Description</label>
                          <textarea
                            value={tariff.conditions_ru || ''}
                            onChange={(e) => updateTariff(serviceIndex, tariffIndex, 'conditions_ru', e.target.value)}
                            className="w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                            placeholder="Описание тарифа..."
                            rows={2}
                          />
                        </div>
                      </div>

                      {/* Tariff Features */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="block text-xs font-medium text-gray-300">🇩🇪 Features</label>
                            <button
                              type="button"
                              onClick={() => addTariffFeature(serviceIndex, tariffIndex, 'de')}
                              className="text-xs text-green-400 hover:text-green-300"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="space-y-1">
                            {(tariff.features_de || ['']).map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-center space-x-1">
                                <input
                                  type="text"
                                  value={feature}
                                  onChange={(e) => updateTariffFeature(serviceIndex, tariffIndex, featureIndex, 'de', e.target.value)}
                                  className="flex-1 px-2 py-1 text-xs bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  placeholder="Feature eingeben..."
                                />
                                {(tariff.features_de || []).length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeTariffFeature(serviceIndex, tariffIndex, featureIndex, 'de')}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="block text-xs font-medium text-gray-300">🇷🇺 Features</label>
                            <button
                              type="button"
                              onClick={() => addTariffFeature(serviceIndex, tariffIndex, 'ru')}
                              className="text-xs text-green-400 hover:text-green-300"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="space-y-1">
                            {(tariff.features_ru || ['']).map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-center space-x-1">
                                <input
                                  type="text"
                                  value={feature}
                                  onChange={(e) => updateTariffFeature(serviceIndex, tariffIndex, featureIndex, 'ru', e.target.value)}
                                  className="flex-1 px-2 py-1 text-xs bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  placeholder="Введите функцию..."
                                />
                                {(tariff.features_ru || []).length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeTariffFeature(serviceIndex, tariffIndex, featureIndex, 'ru')}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={tariff.popular}
                            onChange={(e) => updateTariff(serviceIndex, tariffIndex, 'popular', e.target.checked)}
                            className="form-checkbox h-3 w-3 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                          />
                          <span className="text-xs text-gray-300">Popular Tariff</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No tariffs added yet. Click "Add Tariff" to create pricing options for this service.</p>
              )}
            </div>

            {/* Active Toggle */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={service.active}
                  onChange={(e) => updateService(serviceIndex, 'active', e.target.checked)}
                  className="form-checkbox h-4 w-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-300">Service Active</span>
              </label>
            </div>
          </div>
        ))}
      </div>

      {services.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p>No services yet. Click "Add Service" to create your first service.</p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
        >
          {isSubmitting ? 'Сохранение...' : 'Сохранить услуги'}
        </button>
      </div>
    </form>
  );
};

export default ServicesTab;